import { CommonModule, formatDate } from '@angular/common';
import { Component, OnDestroy, OnInit } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { Router, RouterModule } from '@angular/router';
import { finalize, Subject, debounceTime, takeUntil } from 'rxjs';
import { ButtonModule } from 'primeng/button';
import { CheckboxModule } from 'primeng/checkbox';
import { DatePickerModule } from 'primeng/datepicker';
import { DialogModule } from 'primeng/dialog';
import { IftaLabelModule } from 'primeng/iftalabel';
import { InputGroupModule } from 'primeng/inputgroup';
import { InputGroupAddonModule } from 'primeng/inputgroupaddon';
import { InputTextModule } from 'primeng/inputtext';
import { MultiSelectModule } from 'primeng/multiselect';
import { PaginatorModule, PaginatorState } from 'primeng/paginator';
import { SelectModule } from 'primeng/select';
import { SelectButtonModule } from 'primeng/selectbutton';
import { TableLazyLoadEvent } from 'primeng/table';
import { TableModule } from 'primeng/table';
import { TagModule } from 'primeng/tag';
import { TooltipModule } from 'primeng/tooltip';
import { PageDataPanelComponent } from '../../shared/page-layout/page-data-panel.component';
import { PageHeroComponent } from '../../shared/page-layout/page-hero.component';
import { PageShellComponent } from '../../shared/page-layout/page-shell.component';
import { SessionStorageService } from '../../services/session-storage.service';
import { PermissionService } from '../../security/permission.service';
import { app_permissions } from '../../security/app-permissions';
import {
  TransportOrderRelease,
  ReleaseCancellationImpact,
  TransportOrderReleaseLine,
  TransportOrderReleaseFilterFacets,
  TransportOrderReleaseFilterOption,
  TransportOrderReleaseSearchParams
} from '../transport-planning.model';
import { TransportPlanningService } from '../transport-planning.service';

interface ReleaseFilters {
  release: string;
  sourceNumber: string;
  logisticsProjectIds: number[];
  pickupCountryIds: number[];
  pickupCityIds: number[];
  dropoffCountryIds: number[];
  dropoffCityIds: number[];
  pickupZoneIds: number[];
  includePickupNeighbours: boolean;
  dropoffZoneIds: number[];
  includeDropoffNeighbours: boolean;
  originLocationIds: number[];
  destinationLocationIds: number[];
  crmClientIds: number[];
  needFrom: Date | null;
  needTo: Date | null;
  status: string[];
  quantityMin: number | null;
  quantityMax: number | null;
  uoms: string[];
  regionIds: number[];
  warehouseIds: number[];
  inventoryItemIds: number[];
}

interface FilterOption<T = string | number> {
  label: string;
  value: T;
  metadata?: string | null;
}

interface ActiveFilterChip {
  key: keyof ReleaseFilters;
  label: string;
  value: string | number | boolean;
}

interface SavedReleaseViewState {
  filters: ReleaseFilters;
  first: number;
  tableRows: number;
  viewMode: ReleaseViewMode;
  filtersExpanded: boolean;
}

interface ReleaseGroupView {
  key: string;
  parent: TransportOrderRelease | null;
  anchor: TransportOrderRelease;
  children: TransportOrderRelease[];
  parentReleaseId?: number;
  kind: 'DIRECT' | 'ORCHESTRATED' | 'INTEGRITY_ERROR';
  matchingReleaseIds?: number[];
  integrityMessage?: string;
}

type ReleaseViewMode = 'GROUPED' | 'FLAT';

@Component({
  selector: 'app-transport-release-list',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    RouterModule,
    ButtonModule,
    CheckboxModule,
    DatePickerModule,
    DialogModule,
    IftaLabelModule,
    InputGroupModule,
    InputGroupAddonModule,
    InputTextModule,
    MultiSelectModule,
    PaginatorModule,
    SelectModule,
    SelectButtonModule,
    TableModule,
    TagModule,
    TooltipModule,
    PageShellComponent,
    PageHeroComponent,
    PageDataPanelComponent
  ],
  templateUrl: './transport-release-list.component.html',
  styleUrls: ['./transport-release-list.component.css']
})
export class TransportReleaseListComponent implements OnInit, OnDestroy {
  releases: TransportOrderRelease[] = [];
  filterFacets: TransportOrderReleaseFilterFacets = this.emptyFilterFacets();
  groupedReleases: ReleaseGroupView[] = [];
  viewMode: ReleaseViewMode = 'FLAT';
  readonly viewModeOptions = [
    { label: 'Grouped', value: 'GROUPED' as ReleaseViewMode },
    { label: 'List', value: 'FLAT' as ReleaseViewMode }
  ];
  selected = new Set<number>();
  selectAllMatching = false;
  matchingExclusions = new Set<number>();
  expandedGroups = new Set<string>();
  isLoading = false;
  message = '';
  cancelDialogVisible = false;
  cancelDialogError = '';
  cancellationReason = '';
  cancellationImpact?: ReleaseCancellationImpact;
  cancelTarget?: TransportOrderRelease;
  isCancelling = false;
  rescheduleTarget?: TransportOrderRelease;
  rescheduleNeedBy: Date | null = null;
  rescheduleError = '';
  isPreparingReschedule = false;
  private readonly rescheduleSelections = new Map<number, number>();
  readonly statusFilterOptions: FilterOption[] = [
    { label: 'Draft', value: 'DRAFT' },
    { label: 'Ready', value: 'READY' },
    { label: 'Waiting Previous Leg', value: 'WAITING_PREVIOUS_LEG' },
    { label: 'Split For Cross Dock', value: 'SPLIT_FOR_CROSS_DOCK' },
    { label: 'Released To Shipment', value: 'RELEASED_TO_SHIPMENT' },
    { label: 'Rescheduled', value: 'RESCHEDULED' },
    { label: 'Partially Rescheduled', value: 'PARTIALLY_RESCHEDULED' },
    { label: 'Rejected', value: 'REJECTED' },
    { label: 'Partially Rejected', value: 'PARTIALLY_REJECTED' },
    { label: 'Mixed', value: 'MIXED' },
    { label: 'Partially Mixed', value: 'PARTIALLY_MIXED' },
    { label: 'Validation Error', value: 'VALIDATION_ERROR' },
    { label: 'Cancelled', value: 'CANCELLED' },
    { label: 'Closed', value: 'CLOSED' },
    { label: 'Completed', value: 'COMPLETED' }
  ];
  private readonly deliveryDecisionStatuses = new Set([
    'REJECTED', 'RESCHEDULED', 'MIXED', 'PARTIALLY_REJECTED', 'PARTIALLY_RESCHEDULED', 'PARTIALLY_MIXED'
  ]);
  filters: ReleaseFilters = this.emptyFilters();
  filtersExpanded = false;
  first = 0;
  totalRecords = 0;
  totalGroups = 0;
  unfilteredTotalRecords = 0;
  tableRows = 12;
  readonly rowsPerPageOptions = [12, 25, 50, 100];
  private lastLazyEvent: TableLazyLoadEvent = { first: 0, rows: this.tableRows };
  private pendingLoadRequestKey: string | null = null;
  private latestFacetRequestKey: string | null = null;
  private stableProjectOptions: FilterOption<number>[] = [];
  private pendingProjectOptions: FilterOption<number>[] | null = null;
  private projectFilterOpen = false;
  private matchingReleaseIds = new Set<number>();
  private integrityReleaseIds = new Set<number>();
  private readonly filterChanges$ = new Subject<void>();
  private readonly destroy$ = new Subject<void>();

  constructor(
    private service: TransportPlanningService,
    private session: SessionStorageService,
    private router: Router,
    private permissionService: PermissionService
  ) {}

  canCancelRelease(release: TransportOrderRelease): boolean {
    return !!release.releaseId && !release.parentReleaseId && release.releaseRole !== 'CHILD'
      && release.releaseStatus !== 'CANCELLED'
      && this.permissionService.hasPermission(app_permissions.transport_planning.plans.apply);
  }

  openCancelDialog(release: TransportOrderRelease): void {
    if (!this.canCancelRelease(release) || !release.releaseId) return;
    this.cancelTarget = release;
    this.cancellationImpact = undefined;
    this.cancellationReason = '';
    this.cancelDialogError = '';
    this.cancelDialogVisible = true;
    this.service.getReleaseCancellationImpact(release.releaseId).subscribe({
      next: impact => this.cancellationImpact = impact,
      error: error => this.cancelDialogError = error?.error?.detail || error?.error?.message || 'Could not load cancellation impact.'
    });
  }

  cancelRelease(): void {
    if (!this.cancelTarget?.releaseId || !this.cancellationImpact || this.isCancelling) return;
    const reason = this.cancellationReason.trim();
    if (!reason || reason.length > 500) {
      this.cancelDialogError = 'Enter a reason of 1 to 500 characters.';
      return;
    }
    this.isCancelling = true;
    this.service.cancelRelease(this.cancelTarget.releaseId, reason).subscribe({
      next: () => {
        this.cancelDialogVisible = false;
        this.isCancelling = false;
        this.cancellationImpact?.releaseIds.forEach(id => this.selected.delete(id));
        this.selectAllMatching = false;
        this.matchingExclusions.clear();
        this.loadReleasesLazy({ first: this.first, rows: this.tableRows });
      },
      error: error => {
        this.cancelDialogError = error?.error?.detail || error?.error?.message || 'Could not cancel release.';
        this.isCancelling = false;
      }
    });
  }

  ngOnInit(): void {
    this.restoreViewState();
    this.filterChanges$
      .pipe(debounceTime(250), takeUntil(this.destroy$))
      .subscribe(() => {
        this.selected.clear();
        this.rescheduleSelections.clear();
        this.selectAllMatching = false;
        this.matchingExclusions.clear();
        this.loadReleasesLazy({ first: 0, rows: this.tableRows });
      });
    this.loadReleasesLazy({ first: this.first, rows: this.tableRows });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  loadReleasesLazy(event?: TableLazyLoadEvent): void {
    const companyId = this.companyId();
    if (!companyId) {
      this.message = 'Choose a company first.';
      this.releases = [];
      this.groupedReleases = [];
      this.totalRecords = 0;
      this.totalGroups = 0;
      this.unfilteredTotalRecords = 0;
      this.matchingReleaseIds.clear();
      this.integrityReleaseIds.clear();
      return;
    }

    this.first = event?.first ?? this.lastLazyEvent.first ?? 0;
    this.tableRows = event?.rows ?? this.lastLazyEvent.rows ?? this.tableRows;
    this.lastLazyEvent = { first: this.first, rows: this.tableRows };
    this.saveViewState();

    const page = Math.floor(this.first / this.tableRows);
    const request = this.buildSearchParams(companyId, page, this.tableRows);
    const requestKey = JSON.stringify({ viewMode: this.viewMode, request });
    if (this.pendingLoadRequestKey === requestKey) {
      return;
    }

    this.isLoading = true;
    this.pendingLoadRequestKey = requestKey;
    this.loadFilterFacets(request, requestKey);
    if (this.viewMode === 'GROUPED') {
      this.service.getReleaseGroups(request)
        .pipe(finalize(() => this.finishReleaseLoad(requestKey)))
        .subscribe({
          next: response => {
            if (this.pendingLoadRequestKey !== requestKey) return;
            this.message = '';
            this.groupedReleases = (response.items ?? []).map(group => ({
              key: group.key,
              parent: group.parent ?? null,
              anchor: (group.parent || group.children?.[0]) as TransportOrderRelease,
              children: group.children ?? [],
              parentReleaseId: group.parentReleaseId,
              kind: group.kind as ReleaseGroupView['kind'],
              matchingReleaseIds: group.matchingReleaseIds ?? [],
              integrityMessage: group.integrityMessage
            })).filter(group => !!group.anchor);
            this.releases = this.flattenGroupReleases(this.groupedReleases);
            this.matchingReleaseIds = new Set((response.items ?? [])
              .flatMap(group => group.matchingReleaseIds ?? []));
            this.integrityReleaseIds = new Set(this.groupedReleases
              .filter(group => group.kind === 'INTEGRITY_ERROR')
              .flatMap(group => [group.parent, ...group.children])
              .map(release => release?.releaseId)
              .filter((releaseId): releaseId is number => !!releaseId));
            this.expandedGroups = new Set<string>();
            this.totalRecords = response.totalMatchingReleases ?? 0;
            this.totalGroups = response.totalGroups ?? 0;
            if (this.resetOutOfRangePage(this.totalGroups)) return;
            if (!this.hasActiveFilters()) {
              this.unfilteredTotalRecords = this.totalRecords;
            }
            this.removeUnavailableSelections();
          },
          error: () => {
            if (this.pendingLoadRequestKey === requestKey) {
              this.clearReleaseLoad('Could not load grouped order releases.');
            }
          }
        });
      return;
    }
    this.service.getReleases(request)
      .pipe(finalize(() => this.finishReleaseLoad(requestKey)))
      .subscribe({
        next: response => {
          if (this.pendingLoadRequestKey !== requestKey) return;
          this.message = '';
          this.releases = response.items ?? [];
          this.matchingReleaseIds = new Set(this.releases
            .map(release => release.releaseId)
            .filter((releaseId): releaseId is number => !!releaseId));
          this.integrityReleaseIds.clear();
          this.refreshDisplayGroups();
          this.expandedGroups = new Set<string>();
          this.totalRecords = response.count ?? 0;
          this.totalGroups = this.groupedReleases.length;
          if (this.resetOutOfRangePage(this.totalRecords)) return;
          if (!this.hasActiveFilters()) {
            this.unfilteredTotalRecords = this.totalRecords;
          }
          this.removeUnavailableSelections();
        },
        error: () => {
          if (this.pendingLoadRequestKey === requestKey) {
            this.clearReleaseLoad('Could not load order releases.');
          }
        }
      });
  }

  releasePoolResultText(): string {
    const total = this.totalRecords;
    const baseline = this.hasActiveFilters() ? (this.unfilteredTotalRecords || total) : total;
    const releaseLabel = this.hasActiveFilters() && baseline !== total
      ? `${total} of ${baseline} releases`
      : `${total} releases`;
    if (!this.groupedReleases.length) {
      return releaseLabel;
    }
    return this.viewMode === 'GROUPED'
      ? `${this.totalRecords} matching releases across ${this.totalGroups} groups`
      : `${releaseLabel} in list view on this page`;
  }

  displayedEntryCountLabel(): string {
    return this.viewMode === 'GROUPED'
      ? `${this.groupedReleases.length} groups`
      : `${this.groupedReleases.length} releases`;
  }

  onViewModeChanged(): void {
    this.expandedGroups = new Set<string>();
    this.first = 0;
    this.saveViewState();
    this.loadReleasesLazy({ first: 0, rows: this.tableRows });
  }

  statusOptions(): FilterOption[] {
    return this.filterFacets.statuses.map(value => ({
      label: this.statusFilterOptions.find(option => option.value === value)?.label || value,
      value
    }));
  }

  uomOptions(): FilterOption[] {
    return this.filterFacets.uoms.map(value => ({ label: value, value }));
  }

  projectOptions(): FilterOption<number>[] {
    return this.stableProjectOptions;
  }

  onProjectPanelShow(): void {
    this.projectFilterOpen = true;
  }

  onProjectPanelHide(): void {
    this.projectFilterOpen = false;
    if (this.pendingProjectOptions) {
      this.stableProjectOptions = this.pendingProjectOptions;
      this.pendingProjectOptions = null;
    }
  }

  crmClientOptions(): FilterOption<number>[] {
    return this.filterFacets.crmClients;
  }

  pickupCountryOptions(): FilterOption<number>[] {
    return this.filterFacets.pickupCountries;
  }

  pickupCityOptions(): FilterOption<number>[] {
    return this.filterFacets.pickupCities;
  }

  dropoffCountryOptions(): FilterOption<number>[] {
    return this.filterFacets.dropoffCountries;
  }

  dropoffCityOptions(): FilterOption<number>[] {
    return this.filterFacets.dropoffCities;
  }

  pickupZoneOptions(): FilterOption<number>[] {
    return this.filterFacets.pickupZones;
  }

  dropoffZoneOptions(): FilterOption<number>[] {
    return this.filterFacets.dropoffZones;
  }

  originLocationOptions(): FilterOption<number>[] {
    return this.filterFacets.originLocations;
  }

  destinationLocationOptions(): FilterOption<number>[] {
    return this.filterFacets.destinationLocations;
  }

  regionOptions(): FilterOption<number>[] {
    return this.filterFacets.regions;
  }

  warehouseOptions(): FilterOption<number>[] {
    return this.filterFacets.warehouses;
  }

  warehouseItemOptions(): FilterOption<number>[] {
    return this.filterFacets.inventoryItems;
  }

  activeFilterCount(): number {
    return Object.values(this.filters).filter(value => {
      if (Array.isArray(value)) return value.length > 0;
      if (typeof value === 'boolean') return value;
      return value !== null && value !== '';
    }).length;
  }

  activeFilterChips(): ActiveFilterChip[] {
    const chips: ActiveFilterChip[] = [];
    const addValues = (key: keyof ReleaseFilters, title: string, values: (number | string)[],
      options: FilterOption[] = []) => {
      for (const value of values) {
        const label = options.find(option => option.value === value)?.label || String(value);
        chips.push({ key, label: `${title}: ${label}`, value });
      }
    };
    const numericFilters: [keyof ReleaseFilters, string, FilterOption<number>[]][] = [
      ['logisticsProjectIds', 'Project', this.filterFacets.logisticsProjects],
      ['crmClientIds', 'CRM client', this.filterFacets.crmClients],
      ['pickupCountryIds', 'Pickup country', this.filterFacets.pickupCountries],
      ['pickupCityIds', 'Pickup city', this.filterFacets.pickupCities],
      ['dropoffCountryIds', 'Dropoff country', this.filterFacets.dropoffCountries],
      ['dropoffCityIds', 'Dropoff city', this.filterFacets.dropoffCities],
      ['pickupZoneIds', 'Pickup zone', this.filterFacets.pickupZones],
      ['dropoffZoneIds', 'Dropoff zone', this.filterFacets.dropoffZones],
      ['originLocationIds', 'From location', this.filterFacets.originLocations],
      ['destinationLocationIds', 'To location', this.filterFacets.destinationLocations],
      ['regionIds', 'Source region', this.filterFacets.regions],
      ['warehouseIds', 'Source warehouse', this.filterFacets.warehouses],
      ['inventoryItemIds', 'Warehouse item', this.filterFacets.inventoryItems]
    ];
    for (const [key, title, options] of numericFilters) {
      addValues(key, title, this.filters[key] as number[], options);
    }
    addValues('status', 'Status', this.filters.status, this.statusFilterOptions);
    addValues('uoms', 'UOM', this.filters.uoms);
    for (const [key, title] of [
      ['release', 'Release / tracking'], ['sourceNumber', 'Source number']
    ] as [keyof ReleaseFilters, string][]) {
      const value = this.filters[key] as string;
      if (value.trim()) chips.push({ key, label: `${title}: ${value.trim()}`, value });
    }
    for (const [key, title] of [
      ['needFrom', 'Need from'], ['needTo', 'Need to']
    ] as [keyof ReleaseFilters, string][]) {
      const value = this.filters[key] as Date | null;
      if (value instanceof Date && !Number.isNaN(value.getTime())) {
        chips.push({ key, label: `${title}: ${formatDate(value, 'dd MMM yyyy', 'en-US')}`, value: value.getTime() });
      }
    }
    for (const [key, title] of [
      ['quantityMin', 'Quantity min'], ['quantityMax', 'Quantity max']
    ] as [keyof ReleaseFilters, string][]) {
      const value = this.numberFilter(this.filters[key] as number | null);
      if (value !== null) chips.push({ key, label: `${title}: ${value}`, value });
    }
    if (this.filters.includePickupNeighbours) {
      chips.push({ key: 'includePickupNeighbours', label: 'Include pickup neighbours', value: true });
    }
    if (this.filters.includeDropoffNeighbours) {
      chips.push({ key: 'includeDropoffNeighbours', label: 'Include dropoff neighbours', value: true });
    }
    return chips;
  }

  removeFilterChip(chip: ActiveFilterChip): void {
    const filters = this.filters as unknown as Record<keyof ReleaseFilters, unknown>;
    const current = filters[chip.key];
    if (Array.isArray(current)) {
      filters[chip.key] = current.filter(value => value !== chip.value);
    } else if (typeof current === 'boolean') {
      filters[chip.key] = false;
    } else if (typeof current === 'string') {
      filters[chip.key] = '';
    } else {
      filters[chip.key] = null;
    }
    if (chip.key === 'pickupCountryIds') this.filters.pickupCityIds = [];
    if (chip.key === 'dropoffCountryIds') this.filters.dropoffCityIds = [];
    if (chip.key === 'pickupZoneIds' && !this.filters.pickupZoneIds.length) this.filters.includePickupNeighbours = false;
    if (chip.key === 'dropoffZoneIds' && !this.filters.dropoffZoneIds.length) this.filters.includeDropoffNeighbours = false;
    this.onFiltersChanged();
  }

  onPickupZoneChanged(): void {
    if (!this.filters.pickupZoneIds.length) this.filters.includePickupNeighbours = false;
    this.onFiltersChanged();
  }

  onDropoffZoneChanged(): void {
    if (!this.filters.dropoffZoneIds.length) this.filters.includeDropoffNeighbours = false;
    this.onFiltersChanged();
  }

  resetFilters(): void {
    this.filters = this.emptyFilters();
    this.onFiltersChanged();
  }

  toggleFilters(): void {
    this.filtersExpanded = !this.filtersExpanded;
    this.saveViewState();
  }

  onPageChange(event: PaginatorState): void {
    const first = event.first ?? 0;
    const rows = event.rows ?? this.tableRows;
    if (first === this.first && rows === this.tableRows) {
      return;
    }
    this.loadReleasesLazy({ first, rows });
  }

  onFiltersChanged(): void {
    this.first = 0;
    this.saveViewState();
    this.filterChanges$.next();
  }

  onPickupCountryChanged(): void {
    this.filters.pickupCityIds = [];
    this.onFiltersChanged();
  }

  onDropoffCountryChanged(): void {
    this.filters.dropoffCityIds = [];
    this.onFiltersChanged();
  }

  onRegionChanged(): void {
    this.onFiltersChanged();
  }

  statusSeverity(status?: string): string {
    if (status?.includes('RESCHEDULED')) return 'warn';
    if (status === 'RELEASED_TO_SHIPMENT') return 'success';
    if (status === 'SPLIT_FOR_CROSS_DOCK') return 'warn';
    if (status === 'WAITING_PREVIOUS_LEG') return 'warn';
    if (status === 'VALIDATION_ERROR') return 'danger';
    if (status === 'READY') return 'info';
    return 'secondary';
  }

  releaseRole(release: TransportOrderRelease): string {
    return release.releaseRole || (release.parentReleaseId ? 'CHILD' : 'DIRECT');
  }

  roleSeverity(release: TransportOrderRelease): string {
    const role = this.releaseRole(release);
    if (role === 'PARENT') return 'warn';
    if (role === 'CHILD') return 'info';
    return 'secondary';
  }

  roleLabel(release: TransportOrderRelease): string {
    const role = this.releaseRole(release);
    if (role === 'PARENT') return 'Cross-dock parent';
    if (role === 'CHILD') return this.legLabel(release);
    return 'Direct release';
  }

  legLabel(release: TransportOrderRelease): string {
    if (release.legSequence && release.legCount) {
      return `Leg ${release.legSequence} of ${release.legCount}`;
    }
    return 'Cross-dock leg';
  }

  isGroupExpanded(key: string): boolean {
    return this.expandedGroups.has(key);
  }

  toggleGroup(key: string): void {
    const next = new Set(this.expandedGroups);
    if (next.has(key)) {
      next.delete(key);
    } else {
      next.add(key);
    }
    this.expandedGroups = next;
  }

  groupTitle(group: ReleaseGroupView): string {
    if (this.viewMode === 'FLAT') return 'Order Release';
    if (group.kind === 'DIRECT') return 'Direct Order Release';
    if (group.kind === 'INTEGRITY_ERROR') return 'Release tree integrity issue';
    return 'Original Order Release';
  }

  groupRelease(group: ReleaseGroupView): TransportOrderRelease {
    return group.parent || group.anchor;
  }

  groupChildCountLabel(group: ReleaseGroupView): string {
    const count = group.children.length;
    return `${count} orchestration leg${count === 1 ? '' : 's'}`;
  }

  orchestrationLabel(release: TransportOrderRelease): string {
    if (!release.crossDockOrchestrationId) return 'No orchestration';
    return release.crossDockOrchestrationNumber || `Orchestration #${release.crossDockOrchestrationId}`;
  }

  planningState(release: TransportOrderRelease): string {
    if (this.deliveryDecisionStatuses.has(release.releaseStatus || '')) return release.releaseStatus!;
    if (release.plannedShipmentId || release.releaseStatus === 'RELEASED_TO_SHIPMENT') return 'SHIPMENT_LINKED';
    if (this.hasTruckAssignment(release)) return 'TRUCK_PLANNED';
    if (release.releaseStatus === 'WAITING_PREVIOUS_LEG') return 'WAITING_PREVIOUS_LEG';
    if (release.releaseStatus === 'SPLIT_FOR_CROSS_DOCK') return 'SPLIT_FOR_CROSS_DOCK';
    if (!this.isPlanable(release) || release.validationStatus !== 'READY') return 'BLOCKED';
    return 'READY_FOR_PLANNING';
  }

  planningStateLabel(release: TransportOrderRelease): string {
    const state = this.planningState(release);
    const status = state === 'SHIPMENT_LINKED' ? 'RELEASED_TO_SHIPMENT' : state;
    return this.statusFilterOptions.find(option => option.value === status)?.label
      || status.split('_').map(word => word[0] + word.slice(1).toLowerCase()).join(' ');
  }

  hasInboundTransferGate(group: ReleaseGroupView, release: TransportOrderRelease): boolean {
    if (!release.dependsOnReleaseId) return false;
    return group.children.some(previous => previous.releaseId === release.dependsOnReleaseId
      && !!previous.linkedWarehouseTransferId);
  }

  planningSeverity(release: TransportOrderRelease): string {
    const state = this.planningState(release);
    if (state.includes('RESCHEDULED')) return 'warn';
    if (state.includes('MIXED')) return 'warn';
    if (state === 'SHIPMENT_LINKED') return 'success';
    if (state === 'TRUCK_PLANNED') return 'info';
    if (state === 'READY_FOR_PLANNING') return 'info';
    if (state === 'WAITING_PREVIOUS_LEG' || state === 'SPLIT_FOR_CROSS_DOCK') return 'warn';
    return 'danger';
  }

  groupProgressLabel(group: ReleaseGroupView): string {
    if (!group.children.length) {
      const release = this.groupRelease(group);
      return this.planableReason(release) || release.validationMessage || release.validationStatus || 'Ready for planning';
    }
    const total = group.children.length;
    const shipmentLinked = group.children.filter(release => this.planningState(release) === 'SHIPMENT_LINKED').length;
    const truckPlanned = group.children.filter(release => this.planningState(release) === 'TRUCK_PLANNED').length;
    const ready = group.children.filter(release => this.planningState(release) === 'READY_FOR_PLANNING').length;
    const waiting = group.children.filter(release => this.planningState(release) === 'WAITING_PREVIOUS_LEG').length;
    const blocked = Math.max(total - shipmentLinked - truckPlanned - ready - waiting, 0);
    const parts = [`${shipmentLinked}/${total} shipment-linked`];
    if (truckPlanned) parts.push(`${truckPlanned} truck planned`);
    if (ready) parts.push(`${ready} ready`);
    if (waiting) parts.push(`${waiting} awaiting`);
    if (blocked) parts.push(`${blocked} blocked`);
    return parts.join(' | ');
  }

  isPlanable(release: TransportOrderRelease): boolean {
    return !this.planableReason(release);
  }

  isRescheduled(release: TransportOrderRelease): boolean {
    return release.releaseStatus === 'RESCHEDULED' || release.releaseStatus === 'PARTIALLY_RESCHEDULED';
  }

  planableReason(release: TransportOrderRelease): string {
    if (release.releaseId && this.integrityReleaseIds.has(release.releaseId)) {
      return 'Cross-dock release tree needs repair before this leg can be planned.';
    }
    if (this.releaseRole(release) === 'PARENT' || release.releaseStatus === 'SPLIT_FOR_CROSS_DOCK') {
      return 'Select cross-dock child legs, not the parent release.';
    }
    if (this.isRescheduled(release)) {
      if (release.releaseStatus !== 'RESCHEDULED') return 'Complete the reschedule decision before preparing a replacement.';
      if (!release.plannedShipmentId) return 'Original shipment link is required before replacing rescheduled cargo.';
      if (release.assignmentMode === 'THIRD_PARTY') return 'Third-party releases are not eligible for own-truck planning.';
      return '';
    }
    if (this.deliveryDecisionStatuses.has(release.releaseStatus || '')) return 'Delivery decision completed; release cannot be planned again.';
    if (release.plannedShipmentId || release.releaseStatus === 'RELEASED_TO_SHIPMENT') return 'Already released to shipment.';
    if (release.releaseStatus === 'WAITING_PREVIOUS_LEG') return 'Waiting for the previous cross-dock leg.';
    if (release.releaseStatus === 'CANCELLED' || release.releaseStatus === 'CLOSED' || release.releaseStatus === 'COMPLETED') return 'Release is closed.';
    if (release.validationStatus !== 'READY') return release.validationMessage || 'Validation is not ready.';
    if (release.assignmentMode === 'THIRD_PARTY') return 'Third-party releases are not eligible for own-truck planning.';
    if (release.originLatitude == null || release.originLongitude == null || release.destinationLatitude == null || release.destinationLongitude == null) return 'Origin and destination coordinates are required.';
    if (!this.needDate(release)) return 'Need-by or pickup date is required.';
    return '';
  }

  isSelected(release: TransportOrderRelease): boolean {
    if (!release.releaseId || !this.isCurrentFilterMatch(release)) return false;
    if (this.selectAllMatching && this.isRescheduled(release)) return false;
    return this.selectAllMatching
      ? !this.matchingExclusions.has(release.releaseId)
      : this.selected.has(this.rescheduleSelections.get(release.releaseId) ?? release.releaseId);
  }

  toggleSelection(release: TransportOrderRelease, checked: boolean): void {
    if (!release.releaseId || !this.isCurrentFilterMatch(release) || !this.isPlanable(release)) return;
    if (this.selectAllMatching) {
      if (this.isRescheduled(release)) return;
      checked
        ? this.matchingExclusions.delete(release.releaseId)
        : this.matchingExclusions.add(release.releaseId);
      return;
    }
    if (this.isRescheduled(release)) {
      const replacementId = this.rescheduleSelections.get(release.releaseId);
      if (!checked) {
        if (replacementId) this.selected.delete(replacementId);
      } else if (replacementId) {
        this.selected.add(replacementId);
      } else {
        this.rescheduleTarget = release;
        this.rescheduleNeedBy = null;
        this.rescheduleError = '';
      }
      return;
    }
    checked ? this.selected.add(release.releaseId) : this.selected.delete(release.releaseId);
  }

  useCurrentRescheduleTime(): void {
    this.rescheduleNeedBy = new Date();
    this.rescheduleError = '';
  }

  prepareRescheduledRelease(): void {
    const releaseId = this.rescheduleTarget?.releaseId;
    if (!releaseId || !this.rescheduleNeedBy || this.rescheduleNeedBy.getTime() < Date.now() - 60_000
      || this.isPreparingReschedule) {
      this.rescheduleError = 'Choose the current time or a future need-by date.';
      return;
    }
    this.isPreparingReschedule = true;
    this.service.prepareRescheduledRelease(releaseId, this.rescheduleNeedBy).subscribe({
      next: replacement => {
        this.isPreparingReschedule = false;
        this.rescheduleTarget = undefined;
        if (replacement.releaseId) {
          this.rescheduleSelections.set(releaseId, replacement.releaseId);
          this.selected.add(replacement.releaseId);
          this.loadReleasesLazy({ first: this.first, rows: this.tableRows });
        }
      },
      error: error => {
        this.isPreparingReschedule = false;
        this.rescheduleError = error?.error?.detail || error?.error?.message
          || 'Could not prepare the replacement release.';
      }
    });
  }

  planableVisibleReleases(): TransportOrderRelease[] {
    return this.releases.filter(release => this.isCurrentFilterMatch(release)
      && this.isPlanable(release) && !this.isRescheduled(release));
  }

  readyVisibleReleases(): TransportOrderRelease[] {
    return this.releases.filter(release => this.isCurrentFilterMatch(release)
      && this.planningState(release) === 'READY_FOR_PLANNING');
  }

  allPlanableVisibleSelected(): boolean {
    const rows = this.planableVisibleReleases();
    return rows.length > 0 && rows.every(row => this.isSelected(row));
  }

  allReadyVisibleSelected(): boolean {
    const rows = this.readyVisibleReleases();
    return rows.length > 0 && rows.every(row => this.isSelected(row));
  }

  toggleVisibleSelection(checked: boolean): void {
    for (const release of this.planableVisibleReleases()) {
      if (!release.releaseId) continue;
      this.toggleSelection(release, checked);
    }
  }

  toggleReadyVisibleSelection(checked: boolean): void {
    for (const release of this.readyVisibleReleases()) {
      if (!release.releaseId) continue;
      this.toggleSelection(release, checked);
    }
  }

  toggleAllMatching(checked: boolean): void {
    this.selectAllMatching = checked;
    this.selected.clear();
    this.rescheduleSelections.clear();
    this.matchingExclusions.clear();
  }

  planningSelectionCount(): number {
    return this.selectAllMatching
      ? Math.max(this.totalRecords - this.matchingExclusions.size, 0)
      : this.selected.size;
  }

  planningActionLabel(): string {
    const count = this.planningSelectionCount();
    return count ? `Run Bulk Plan (${count})` : 'Run Bulk Plan';
  }

  truckPlanningActionLabel(): string {
    const count = this.selectAllMatching ? 0 : this.selected.size;
    return count ? `Build Truck Plan (${count})` : 'Build Truck Plan';
  }

  addToShipmentActionLabel(): string {
    const count = this.selectAllMatching ? 0 : this.selected.size;
    return count ? `Add to Shipment (${count})` : 'Add to Shipment';
  }

  parentReference(release: TransportOrderRelease): string {
    if (!release.parentReleaseId) return '';
    const parent = this.releases.find(row => row.releaseId === release.parentReleaseId);
    return parent?.customerTrackingNumber || parent?.releaseNumber || `Parent #${release.parentReleaseId}`;
  }

  customerLabel(release: TransportOrderRelease): string {
    return release.crmClientName
      || release.companyName
      || release.customerName
      || (release.crmClientId ? `CRM Client #${release.crmClientId}` : '')
      || (release.companyId ? `Company #${release.companyId}` : '')
      || (release.customerId ? `Customer #${release.customerId}` : '')
      || 'Customer missing';
  }

  accountLabel(release: TransportOrderRelease): string {
    return release.crmAccountName
      || (release.crmAccountId ? `Account #${release.crmAccountId}` : '')
      || '';
  }

  logisticsProjectLabel(release: TransportOrderRelease): string {
    return release.logisticsProjectNumber
      || (release.logisticsProjectId ? `#${release.logisticsProjectId}` : '');
  }

  linkedShipmentLabel(release: TransportOrderRelease): string {
    return release.plannedShipmentId ? `Shipment ${release.plannedShipmentId}` : 'No shipment linked';
  }

  releaseWindowLabel(release: TransportOrderRelease): string {
    return `Pickup ${this.formatDate(release.pickupWindowStart || release.pickupWindowEnd)}`;
  }

  deliveryWindowLabel(release: TransportOrderRelease): string {
    return `Delivery ${this.formatDate(release.deliveryWindowStart || release.deliveryWindowEnd)}`;
  }

  assignmentSummary(release: TransportOrderRelease): string {
    return [
      release.assignmentMode || 'OWN_TRUCK',
      release.vehicleTypeName,
      release.helperCount != null ? `${release.helperCount} helper${release.helperCount === 1 ? '' : 's'}` : null,
      release.manpowerCount != null ? `${release.manpowerCount} crew` : null
    ].filter(Boolean).join(' | ');
  }

  sourceReferenceLabel(release: TransportOrderRelease): string {
    if (release.sourceType === 'POSM_TASK' && release.posmDeliveryScheduleNumber) {
      return release.posmDeliveryScheduleNumber;
    }
    return [release.sourceType, release.sourceNumber || release.sourceId || '']
      .filter(Boolean)
      .join(' ');
  }

  customerMeta(release: TransportOrderRelease): string {
    const values = [
      release.crmClientId ? `CRM ${release.crmClientId}` : null,
      release.crmAccountId ? `Account ${release.crmAccountId}` : null,
      release.crmClientContactId ? `CRM Contact ${release.crmClientContactId}` : null,
      release.companyId ? `Company ${release.companyId}` : null,
      release.customerId ? `Contact ${release.customerId}` : null
    ].filter(Boolean);
    return values.join(' / ');
  }

  hasCustomerContext(release: TransportOrderRelease): boolean {
    return !!(release.crmClientId || release.crmAccountId || release.customerId || release.companyId);
  }

  routeSummary(release: TransportOrderRelease): string {
    return [release.originName || 'Start place missing', release.destinationName || 'End place missing']
      .filter(Boolean)
      .join(' to ');
  }

  collapsedPickupLabel(release: TransportOrderRelease): string {
    return this.locationHeadline(release.originLocationId, release.originName, 'Pickup missing');
  }

  collapsedDropoffLabel(release: TransportOrderRelease): string {
    return this.locationHeadline(release.destinationLocationId, release.destinationName, 'Dropoff missing');
  }

  collapsedPickupMeta(release: TransportOrderRelease): string {
    return this.locationMeta(release.originLocationId);
  }

  collapsedDropoffMeta(release: TransportOrderRelease): string {
    return this.locationMeta(release.destinationLocationId);
  }

  itemName(line: TransportOrderReleaseLine): string {
    return line.itemName || line.itemDescription || 'Unnamed item';
  }

  itemSizeLabel(line: TransportOrderReleaseLine): string {
    const size = line.itemSize?.trim();
    if (!size) return 'Size not set';
    const dimensions = size.match(/^(\d+(?:\.\d+)?)\s*[x×]\s*(\d+(?:\.\d+)?)\s*[x×]\s*(\d+(?:\.\d+)?)(?:\s*m)?$/i);
    if (!dimensions) return `Size: ${size}`;
    if (dimensions.slice(1).every(value => Number(value) === 0)) return 'Size not set';
    return `L × B × H: ${dimensions.slice(1).join(' × ')} m`;
  }

  itemVolumeLabel(line: TransportOrderReleaseLine): string {
    const volume = Number(line.volumeCbm);
    return Number.isFinite(volume) && volume > 0
      ? `Total volume: ${new Intl.NumberFormat('en-US', { maximumSignificantDigits: 4 }).format(volume)} CBM`
      : '';
  }

  releaseExecutionNote(release: TransportOrderRelease): string {
    return this.planableReason(release) || release.validationMessage || release.validationStatus || 'Ready for planning';
  }

  formatDate(value?: string | Date): string {
    return value ? formatDate(value, 'dd MMM yyyy HH:mm', 'en-US') : 'Not set';
  }

  needByLabel(release: TransportOrderRelease): string {
    return this.formatDate(this.needDate(release) || undefined);
  }

  needByState(release: TransportOrderRelease): 'normal' | 'today' | 'overdue' | 'missing' {
    const needBy = this.needDate(release);
    if (!needBy) return 'missing';
    const now = new Date();
    if (needBy.getTime() < now.getTime()) return 'overdue';
    const endOfToday = new Date(now);
    endOfToday.setHours(23, 59, 59, 999);
    return needBy.getTime() <= endOfToday.getTime() ? 'today' : 'normal';
  }

  releaseLines(release: TransportOrderRelease): TransportOrderReleaseLine[] {
    return release.lines ?? [];
  }

  loadSummary(release: TransportOrderRelease): string {
    const lines = this.releaseLines(release);
    if (!lines.length) return 'Load details not set';
    const quantity = this.totalQuantity(lines);
    const quantityText = quantity > 0 ? `${this.formatAmount(quantity)} ${this.sharedUom(lines)}` : `${lines.length} line${lines.length === 1 ? '' : 's'}`;
    if (lines.length === 1) {
      return `${quantityText} - ${this.lineTitle(lines[0])}`;
    }
    return `${quantityText} across ${lines.length} lines`;
  }

  lineTitle(line: TransportOrderReleaseLine): string {
    return [
      line.itemNumber || line.itemCode,
      this.lineSkuLabel(line),
      line.itemName || line.itemDescription
    ]
      .filter(value => !!value)
      .join(' - ') || 'Transport demand';
  }

  lineQuantity(line: TransportOrderReleaseLine): string {
    const amount = Number(line.quantity ?? line.units ?? 0);
    const value = amount > 0 ? this.formatAmount(amount) : '-';
    return `${value} ${line.uom?.toUpperCase() === 'SQM' ? 'sqm' : line.uom || 'unit'}`;
  }

  lineMeta(line: TransportOrderReleaseLine): string {
    return [
      this.itemSizeLabel(line),
      this.itemVolumeLabel(line),
      line.actionRequired,
      line.sourceLineType
    ].filter(Boolean).join(' / ') || 'Source line';
  }

  lineSkuLabel(line: TransportOrderReleaseLine): string | null {
    const sku = line.skuNumber || line.inventoryStockUnitCode;
    return sku ? `SKU ${sku}` : null;
  }

  private locationHeadline(locationId?: number | null, fallbackName?: string | null, emptyLabel = 'Location missing'): string {
    const location = this.locationOptionById(locationId);
    return fallbackName
      || location?.label
      || (locationId ? `Location #${locationId}` : emptyLabel);
  }

  private locationMeta(locationId?: number | null): string {
    return this.locationOptionById(locationId)?.metadata || 'Location details unavailable';
  }

  private locationOptionById(locationId?: number | null): TransportOrderReleaseFilterOption | undefined {
    if (!locationId) return undefined;
    return this.filterFacets.originLocations.find(location => location.value === locationId)
      || this.filterFacets.destinationLocations.find(location => location.value === locationId);
  }

  private needDate(release: TransportOrderRelease): Date | null {
    const value = release.deliveryWindowEnd || release.pickupWindowStart;
    if (!value) return null;
    const date = value instanceof Date ? value : new Date(value);
    return Number.isNaN(date.getTime()) ? null : date;
  }

  isCurrentFilterMatch(release: TransportOrderRelease): boolean {
    return !!release.releaseId && this.matchingReleaseIds.has(release.releaseId);
  }

  paginationTotalRecords(): number {
    return this.viewMode === 'GROUPED' ? this.totalGroups : this.totalRecords;
  }

  paginationEntryLabel(): string {
    return this.viewMode === 'GROUPED' ? 'groups' : 'releases';
  }

  private flattenGroupReleases(groups: ReleaseGroupView[]): TransportOrderRelease[] {
    const rows = new Map<number, TransportOrderRelease>();
    for (const group of groups) {
      const candidates = [group.parent, ...group.children];
      for (const release of candidates) {
        if (release?.releaseId) rows.set(release.releaseId, release);
      }
    }
    return Array.from(rows.values());
  }

  private removeUnavailableSelections(): void {
    for (const row of this.releases) {
      if (row.releaseId && (!this.isCurrentFilterMatch(row) || !this.isPlanable(row))) {
        this.selected.delete(row.releaseId);
        this.matchingExclusions.delete(row.releaseId);
      }
    }
  }

  private clearReleaseLoad(message: string): void {
    this.message = message;
    this.releases = [];
    this.groupedReleases = [];
    this.totalRecords = 0;
    this.totalGroups = 0;
    this.matchingReleaseIds.clear();
    this.integrityReleaseIds.clear();
  }

  private finishReleaseLoad(requestKey: string): void {
    if (this.pendingLoadRequestKey !== requestKey) return;
    this.pendingLoadRequestKey = null;
    this.isLoading = false;
  }

  createRelease(): void {
    this.router.navigate(['/transport-planning/releases/new']);
  }

  planLoads(): void {
    if (!this.selectAllMatching && !this.selected.size) {
      this.message = 'Select specific releases, or choose all matching releases, before running a bulk plan.';
      return;
    }
    const releaseIds = this.selectAllMatching
      ? Array.from(this.matchingExclusions)
      : Array.from(this.selected);
    this.router.navigate(['/transport-planning/bulk-planning/new'], {
      queryParams: {
        releaseIds: releaseIds.length ? releaseIds.join(',') : null,
        selectionMode: this.selectAllMatching ? 'FILTER_SNAPSHOT' : 'EXPLICIT_IDS',
        filter: this.selectAllMatching ? JSON.stringify(this.selectionFilterSnapshot()) : null
      }
    });
  }

  buildTruckPlan(): void {
    const releaseIds = Array.from(this.selected);
    if (!releaseIds.length || this.selectAllMatching) {
      this.message = 'Select specific releases to build a truck plan.';
      return;
    }
    this.router.navigate(['/transport-planning/plans/create'], {
      queryParams: { releaseIds: releaseIds.join(',') }
    });
  }

  addToShipment(): void {
    const releaseIds = Array.from(this.selected);
    if (!releaseIds.length || this.selectAllMatching) {
      this.message = 'Select specific releases to add to an existing shipment.';
      return;
    }
    this.router.navigate(['/transport-planning/releases/add-to-shipment'], {
      queryParams: { releaseIds: releaseIds.join(',') }
    });
  }

  private selectionFilterSnapshot(): Record<string, unknown> {
    return {
      release: this.normalizeReleaseFilter(this.blankToNull(this.filters.release)),
      sourceNumber: this.blankToNull(this.filters.sourceNumber),
      logisticsProjectIds: this.filters.logisticsProjectIds,
      pickupCountryIds: this.filters.pickupCountryIds,
      pickupCityIds: this.filters.pickupCityIds,
      dropoffCountryIds: this.filters.dropoffCountryIds,
      dropoffCityIds: this.filters.dropoffCityIds,
      pickupZoneIds: this.filters.pickupZoneIds,
      includePickupNeighbours: this.filters.includePickupNeighbours,
      dropoffZoneIds: this.filters.dropoffZoneIds,
      includeDropoffNeighbours: this.filters.includeDropoffNeighbours,
      originLocationIds: this.filters.originLocationIds,
      destinationLocationIds: this.filters.destinationLocationIds,
      crmClientIds: this.filters.crmClientIds,
      needFrom: this.filters.needFrom,
      needTo: this.filters.needTo,
      statuses: this.filters.status,
      quantityMin: this.numberFilter(this.filters.quantityMin),
      quantityMax: this.numberFilter(this.filters.quantityMax),
      uoms: this.filters.uoms,
      regionIds: this.filters.regionIds,
      warehouseIds: this.filters.warehouseIds,
      inventoryItemIds: this.filters.inventoryItemIds
    };
  }

  private companyId(): number {
    return this.session.getCompany()?.id ?? 0;
  }

  private viewStateKey(): string | null {
    const userId = this.session.getCustomer()?.id ?? this.session.getUser()?.id;
    const companyId = this.companyId();
    return userId && companyId ? `transport-release-list:v1:${userId}:${companyId}` : null;
  }

  private saveViewState(): void {
    const key = this.viewStateKey();
    if (!key) return;
    try {
      const state: SavedReleaseViewState = {
        filters: this.filters,
        first: this.first,
        tableRows: this.tableRows,
        viewMode: this.viewMode,
        filtersExpanded: this.filtersExpanded
      };
      window.sessionStorage.setItem(key, JSON.stringify(state));
    } catch {
      // The list still works when browser storage is unavailable.
    }
  }

  private restoreViewState(): void {
    const key = this.viewStateKey();
    if (!key) return;
    try {
      const stored = window.sessionStorage.getItem(key);
      if (!stored) return;
      const state = JSON.parse(stored) as SavedReleaseViewState;
      if (!state || !state.filters || !Number.isInteger(state.first) || state.first < 0
        || !this.rowsPerPageOptions.includes(state.tableRows)
        || state.first % state.tableRows !== 0
        || !['GROUPED', 'FLAT'].includes(state.viewMode)
        || typeof state.filtersExpanded !== 'boolean') throw new Error('Invalid release list state');
      const defaults = this.emptyFilters();
      const restored = {} as Record<keyof ReleaseFilters, unknown>;
      const saved = state.filters as unknown as Record<keyof ReleaseFilters, unknown>;
      for (const key of Object.keys(defaults) as (keyof ReleaseFilters)[]) {
        const value = saved[key];
        if (key === 'needFrom' || key === 'needTo') {
          if (value !== null && typeof value !== 'string') throw new Error('Invalid date filter');
          const date = value === null ? null : new Date(value);
          if (date && Number.isNaN(date.getTime())) throw new Error('Invalid date filter');
          restored[key] = date;
        } else if (Array.isArray(defaults[key])) {
          if (!Array.isArray(value) || !value.every(item =>
            key === 'status' || key === 'uoms'
              ? typeof item === 'string'
              : Number.isSafeInteger(item) && item > 0)) throw new Error('Invalid list filter');
          restored[key] = value;
        } else if (typeof defaults[key] === 'string' || typeof defaults[key] === 'boolean') {
          if (typeof value !== typeof defaults[key]) throw new Error('Invalid filter');
          restored[key] = value;
        } else {
          if (value !== null && (typeof value !== 'number' || !Number.isFinite(value))) {
            throw new Error('Invalid numeric filter');
          }
          restored[key] = value;
        }
      }
      const legacyDefaultStatuses = [
        'DRAFT', 'READY', 'WAITING_PREVIOUS_LEG', 'SPLIT_FOR_CROSS_DOCK',
        'RELEASED_TO_SHIPMENT', 'VALIDATION_ERROR'
      ];
      if (Array.isArray(restored.status) && restored.status.length === legacyDefaultStatuses.length
        && legacyDefaultStatuses.every(status => (restored.status as string[]).includes(status))) {
        restored.status = [];
      }
      this.filters = restored as unknown as ReleaseFilters;
      this.first = state.first;
      this.tableRows = state.tableRows;
      this.viewMode = state.viewMode;
      this.filtersExpanded = state.filtersExpanded;
    } catch {
      try {
        window.sessionStorage.removeItem(key);
      } catch {
        // Ignore unavailable browser storage.
      }
    }
  }

  private resetOutOfRangePage(total: number): boolean {
    if (this.first === 0 || this.first < total) return false;
    this.first = 0;
    this.loadReleasesLazy({ first: 0, rows: this.tableRows });
    return true;
  }

  private hasTruckAssignment(release: TransportOrderRelease): boolean {
    return !!(release.assignedVehicleId || release.assignedDriverId || release.assignedMobilizationId);
  }

  private hasActiveFilters(): boolean {
    return this.activeFilterCount() > 0;
  }

  private refreshDisplayGroups(): void {
    this.groupedReleases = this.buildFlatReleaseGroups(this.releases);
  }

  private buildFlatReleaseGroups(releases: TransportOrderRelease[]): ReleaseGroupView[] {
    return this.sortedReleases(releases).map((release, index) => ({
      key: `flat-${release.releaseId || release.releaseNumber || index}`,
      parent: release,
      anchor: release,
      children: [],
      parentReleaseId: release.parentReleaseId || release.releaseId,
      kind: 'DIRECT'
    }));
  }

  private sortedReleases(releases: TransportOrderRelease[]): TransportOrderRelease[] {
    return [...releases].sort((left, right) => {
      const leftRole = this.releaseRole(left);
      const rightRole = this.releaseRole(right);
      const leftParentKey = left.parentReleaseId || left.releaseId || 0;
      const rightParentKey = right.parentReleaseId || right.releaseId || 0;
      if (leftParentKey !== rightParentKey) return leftParentKey - rightParentKey;
      if (leftRole !== rightRole) {
        if (leftRole === 'PARENT') return -1;
        if (rightRole === 'PARENT') return 1;
      }
      const leftLeg = left.legSequence || 0;
      const rightLeg = right.legSequence || 0;
      if (leftLeg !== rightLeg) return leftLeg - rightLeg;
      return `${left.releaseNumber || ''}`.localeCompare(`${right.releaseNumber || ''}`);
    });
  }

  private buildSearchParams(
    owningCompanyId: number,
    page: number,
    pageSize: number
  ): TransportOrderReleaseSearchParams {
    return {
      owningCompanyId,
      page,
      pageSize,
      release: this.normalizeReleaseFilter(this.blankToNull(this.filters.release)),
      sourceNumber: this.blankToNull(this.filters.sourceNumber),
      logisticsProjectIds: this.filters.logisticsProjectIds,
      pickupCountryIds: this.filters.pickupCountryIds,
      pickupCityIds: this.filters.pickupCityIds,
      dropoffCountryIds: this.filters.dropoffCountryIds,
      dropoffCityIds: this.filters.dropoffCityIds,
      pickupZoneIds: this.filters.pickupZoneIds,
      includePickupNeighbours: this.filters.includePickupNeighbours,
      dropoffZoneIds: this.filters.dropoffZoneIds,
      includeDropoffNeighbours: this.filters.includeDropoffNeighbours,
      originLocationIds: this.filters.originLocationIds,
      destinationLocationIds: this.filters.destinationLocationIds,
      crmClientIds: this.filters.crmClientIds,
      needFrom: this.filters.needFrom,
      needTo: this.filters.needTo,
      status: this.statusQueryValue(),
      quantityMin: this.numberFilter(this.filters.quantityMin),
      quantityMax: this.numberFilter(this.filters.quantityMax),
      uoms: this.filters.uoms,
      regionIds: this.filters.regionIds,
      warehouseIds: this.filters.warehouseIds,
      inventoryItemIds: this.filters.inventoryItemIds
    };
  }

  private emptyFilters(): ReleaseFilters {
    return {
      release: '',
      sourceNumber: '',
      logisticsProjectIds: [],
      pickupCountryIds: [],
      pickupCityIds: [],
      dropoffCountryIds: [],
      dropoffCityIds: [],
      pickupZoneIds: [],
      includePickupNeighbours: false,
      dropoffZoneIds: [],
      includeDropoffNeighbours: false,
      originLocationIds: [],
      destinationLocationIds: [],
      crmClientIds: [],
      needFrom: null,
      needTo: null,
      status: [],
      quantityMin: null,
      quantityMax: null,
      uoms: [],
      regionIds: [],
      warehouseIds: [],
      inventoryItemIds: []
    };
  }

  private normalizeReleaseFilter(value: string | null): string | null {
    return value ? value.replace(/\//g, '_') : value;
  }

  private loadFilterFacets(request: TransportOrderReleaseSearchParams, requestKey: string): void {
    this.latestFacetRequestKey = requestKey;
    this.service.getReleaseFilterFacets(request)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: facets => {
          if (this.latestFacetRequestKey !== requestKey) return;
          this.filterFacets = {
            ...this.emptyFilterFacets(),
            ...facets
          };
          if (this.projectFilterOpen && this.stableProjectOptions.length) {
            this.pendingProjectOptions = this.filterFacets.logisticsProjects;
          } else {
            this.stableProjectOptions = this.filterFacets.logisticsProjects;
          }
        },
        error: () => {
          if (this.latestFacetRequestKey === requestKey) {
            this.filterFacets = this.emptyFilterFacets();
            if (this.projectFilterOpen) {
              this.pendingProjectOptions = [];
            } else {
              this.stableProjectOptions = [];
            }
          }
        }
      });
  }

  private emptyFilterFacets(): TransportOrderReleaseFilterFacets {
    return {
      logisticsProjects: [],
      crmClients: [],
      pickupCountries: [],
      pickupCities: [],
      dropoffCountries: [],
      dropoffCities: [],
      pickupZones: [],
      dropoffZones: [],
      originLocations: [],
      destinationLocations: [],
      regions: [],
      warehouses: [],
      inventoryItems: [],
      uoms: [],
      statuses: []
    };
  }

  private numberFilter(value: number | string | null): number | null {
    if (value === null || value === '') return null;
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : null;
  }

  private blankToNull(value: string | null): string | null {
    const trimmed = value?.trim();
    return trimmed ? trimmed : null;
  }

  private statusQueryValue(): string | null {
    const values = Array.from(new Set((this.filters.status || []).filter(Boolean)));
    return values.length ? values.join(',') : null;
  }

  private totalQuantity(lines: TransportOrderReleaseLine[]): number {
    return lines.reduce((sum, line) => sum + Number(line.quantity ?? line.units ?? 0), 0);
  }

  private sharedUom(lines: TransportOrderReleaseLine[]): string {
    const values = Array.from(new Set(lines.map(line => line.uom).filter(Boolean)));
    return values.length === 1 ? values[0] || 'unit' : 'units';
  }

  private formatAmount(value: number): string {
    return new Intl.NumberFormat('en-US', { maximumFractionDigits: 2 }).format(value);
  }
}
